import { Box, Stack } from '@mui/material';
import { useCallback, useMemo, useState, useEffect } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import * as poiState from '../../../../state';
import { useAllPaintRegionsForFilterAndSearchPanelQuery } from '@/__generated__/graphql';
import { useGeneralFields, useTriggerPoiEditing } from '../../hooks';
import { GeneralFieldsForm } from '../GeneralFieldsForm';
import {
  PoiAccessibilityEnum,
  useUpdatePoiGeneralFieldsMutation,
  useGetAllTeamsForCreateOrEditPoiQuery,
  AllPointOfInterestsForDataLoaderDocument,
  SelectedPointOfInterestDocument,
  PointOfInterestsCountByStatusDocument,
  CuboidInput,

} from '@/__generated__/graphql';
import { StyledForm } from './styles';
import { SaveCancelFormButtons } from '../SaveCancelFormButtons';
import { convertTimeStampToDate, upperSnakeCase } from '@/utils';
import { enumToString } from '@/utils/string';
import { saveCancelButtonCommonStyles } from '../../../styles';
import { useNavbarHeight } from '@/hooks/useNavbarHeight';
import * as cuboidsState from '../../../../../cuboids/state';
import { useAnalysisCuboidActions } from '../../../../../cuboids/hooks';
import { AnalysisCuboid } from '@/types';
import { LinkedPoiEditor } from '../LinkedPoiEditor';
import { PoiFormSubHeading } from '../styles';
import * as state from '@/components/Analysis/state';
import { allVoiTemplates as allVoiTemplatesState } from '@/components/Analysis/state';
import { useIsPoiWorkScope } from '@/hooks/useIsPoiWorkScope';
import { useGetStructureId } from '@/hooks/useGetStructureId';

export const UpdateGeneralFields = () => {
  const editPointOfInterest = useAtomValue(poiState.editPointOfInterest);
  const setPoiFilteredByCheckbox = useSetAtom(poiState.poiFilteredByCheckbox);
  const currentCuboid = useAtomValue(cuboidsState.currentCuboid);
  const isCuboidEditorEnabled = useAtomValue(cuboidsState.isCuboidEditorEnabled);
  const allVoiTemplates = useAtomValue(allVoiTemplatesState);

  const { pointOfInterest: initialPoi } = editPointOfInterest || {};
  const [loading, setLoading] = useState(false);
  const navbarHeight = useNavbarHeight();
  const { checkByType } = useIsPoiWorkScope();

  // Get structure ID using the dedicated hook
  const structureId = useGetStructureId();
  
  // Log the structure ID when it's available
  useEffect(() => {
    if (structureId) {
      console.log('Structure ID from useGetStructureId hook:', structureId);
    }
  }, [structureId]);

  // Use the initial POI data
  const poi = initialPoi;
  
  // Get paint regions using the structureId from the hook
  const { 
    data: paintRegionsData, 
    loading: paintRegionsLoading, 
    error: paintRegionsError 
  } = useAllPaintRegionsForFilterAndSearchPanelQuery({
    variables: { 
      structureId: structureId || ''
    },
    fetchPolicy: 'cache-first',
    skip: !structureId, // Skip if structureId is not available
    onCompleted: (data) => {
      console.log('Fetched paint regions:', data);
      console.log('Structure ID used for query:', structureId);
    },
    onError: (error) => {
      console.warn('Error fetching paint regions, will continue without them:', error);
      // Continue without paint regions rather than showing an error
    }
  });

  // Set up paint region state and handlers - same as in CreatePoi
  const [selectedPaintRegionId, setSelectedPaintRegionId] = useState<string>('');
  const setSelectedPaintRegionIds = useSetAtom(state.selectedFilteredPaintRegionIds);

  // Handle paint region selection - same as in CreatePoi
  const handlePaintRegionChange = useCallback(
    (paintRegionName: string) => {
      const paintRegion = paintRegionsData?.allPaintRegions?.find(
        (region) => region.name === paintRegionName
      );
      if (paintRegion) {
        setSelectedPaintRegionId(paintRegion.id);
      }
    },
    [paintRegionsData?.allPaintRegions]
  );

  // Update paint region filter - same as in CreatePoi
  const updatePaintRegionFilter = useCallback(
    (paintRegionId: string) => {
      setSelectedPaintRegionIds((prevIds: string[]) => {
        // Only add if it's not already in the list
        if (!prevIds.includes(paintRegionId)) {
          return [...prevIds, paintRegionId];
        }
        return prevIds;
      });
    },
    [setSelectedPaintRegionIds]
  );
  
  // Update selectedPaintRegionId when POI or paintRegion data changes
  useEffect(() => {
    // If we have a paint region on the POI, use it
    if (poi?.paintRegion?.name) {
      // Find matching paint region by name since we might not have the ID
      const matchingRegion = paintRegionsData?.allPaintRegions?.find(
        r => r.name === poi.paintRegion?.name
      );
      
      if (matchingRegion) {
        setSelectedPaintRegionId(matchingRegion.id);
        updatePaintRegionFilter(matchingRegion.id);
      } else if (poi.paintRegion.id) {
        // Fallback to using ID directly if name doesn't match but we have an ID
        setSelectedPaintRegionId(poi.paintRegion.id);
        updatePaintRegionFilter(poi.paintRegion.id);
      }
    } else if (paintRegionsData?.allPaintRegions?.length) {
      setSelectedPaintRegionId('');
    }
  }, [poi?.paintRegion, paintRegionsData, paintRegionsLoading, paintRegionsError, updatePaintRegionFilter]);
  
  // Generate paint region options for the dropdown - same as in CreatePoi
  const paintRegionOptions = useMemo(() => {
    const options = paintRegionsData?.allPaintRegions?.map((paintRegion) => ({
      label: paintRegion.name,
      value: paintRegion.id,
    })) || [];
    
    console.log('Generated paint region options:', options);
    return options;
  }, [paintRegionsData?.allPaintRegions]);

  const formattedDueDate = poi?.dueDate ? convertTimeStampToDate(poi?.dueDate) : undefined;

  const modifyCuboidDataInPoi = useCallback(
    (updatedData: AnalysisCuboid, targetId: string) => {
      setPoiFilteredByCheckbox((current) => {
        const copy = [...current];
        const updatedPoiIndex = copy.findIndex(({ id }) => id === targetId);
        if (updatedPoiIndex === -1) return current;

        const updatedPoi = {
          ...copy[updatedPoiIndex],
          cuboids: [updatedData],
        };

        copy[updatedPoiIndex] = updatedPoi;

        return copy;
      });
    },
    [setPoiFilteredByCheckbox]
  );

  const generalFieldsData = useGeneralFields({
    assignee: poi?.assignee || undefined,
    dueDate: formattedDueDate,
    name: poi?.name || undefined,
    poiId: poi?.poiId || undefined,
    status: poi?.status || undefined,
    visibility: poi?.visibility || undefined,
    teamIds: poi?.teams?.map(({ id }) => id) || [],
    workpackNumber: poi?.workpackNumber || undefined,
    comment: poi?.comment || undefined,
    accessibility: poi?.accessibility || undefined,
  });
  const { data: teamsData, loading: teamsLoading } = useGetAllTeamsForCreateOrEditPoiQuery();

  const teamOptions = useMemo(() => {
    return teamsData?.allTeams?.map(({ id, name }) => ({
      label: name,
      value: id,
    }));
  }, [teamsData?.allTeams]);

  const [mutate] = useUpdatePoiGeneralFieldsMutation({});

  const { showSnackBar } = useSnackBarMessage({});

  const { cancelEditing } = useTriggerPoiEditing();

  const { stopEditingPoiCuboids, doneEditingPoiCuboids } = useAnalysisCuboidActions();

  const isVoi = allVoiTemplates.some(
    (voiTemplate) => voiTemplate.toLowerCase() === enumToString(poi?.type ?? '').toLowerCase()
  );

  const isWorkScope = checkByType(poi?.type || '');

  const shouldIncludeCuboids = useMemo(() => {
    if (!currentCuboid) return false;
    return isWorkScope || isVoi;
  }, [isVoi, isWorkScope, currentCuboid]);

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLElement>) => {
      event.preventDefault();
      setLoading(true);
      const stringifiedDueDate = generalFieldsData.state.dueDate
        ? String(generalFieldsData.state.dueDate)
        : undefined;

      const accessibilityEnum = Object.values(PoiAccessibilityEnum).find(
        (accessibilityValue) =>
          accessibilityValue === upperSnakeCase(generalFieldsData.state.accessibility || '')
      );
      // Do not pass poiId for update mutation since this id is not user defined
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { poiId, ...generalFieldsState } = generalFieldsData.state;
      // Include paint region in the update if it has changed - same as in CreatePoi
      const paintRegionInput = selectedPaintRegionId && selectedPaintRegionId !== poi?.paintRegion?.id
        ? { paintRegionId: selectedPaintRegionId }
        : {};
      
      // Update paint region filter if a new region was selected
      if (paintRegionInput.paintRegionId) {
        updatePaintRegionFilter(paintRegionInput.paintRegionId);
      }
        
      mutate({
        variables: {
          input: {
            pointOfInterestId: poi?.id || '',
            ...generalFieldsState,
            ...paintRegionInput,
            dueDate: stringifiedDueDate || undefined,
            ...(accessibilityEnum ? { accessibility: accessibilityEnum } : {}),
            cuboids: shouldIncludeCuboids
              ? [
                  {
                    position: currentCuboid?.position,
                    scale: currentCuboid?.scale,
                    rotation: currentCuboid?.rotation,
                    // currentCuboid is already check for undefined
                    // in `shouldIncludeCuboids`
                  } as CuboidInput,
                ]
              : undefined,
            lastModifiedFor: 'Update General Fields',
          },
        },
        refetchQueries: [
          SelectedPointOfInterestDocument,
          AllPointOfInterestsForDataLoaderDocument,
          PointOfInterestsCountByStatusDocument,
        ],
      })
        .then((response) => {
          const newCuboids = response?.data?.updatePointOfInterest?.cuboids;
          if (newCuboids) {
            modifyCuboidDataInPoi(newCuboids[0] as AnalysisCuboid, poi?.id || '');
          }
          showSnackBar(`${isWorkScope ? 'Work scope' : 'Point of interest'} updated`, 'success');
          cancelEditing();
          doneEditingPoiCuboids();
        })
        .catch(() => {
          showSnackBar(
            `Could not update ${isWorkScope ? 'work scope' : 'point of interest'}`,
            'error'
          );
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [
      generalFieldsData.state,
      mutate,
      poi?.id,
      currentCuboid,
      showSnackBar,
      isWorkScope,
      cancelEditing,
      doneEditingPoiCuboids,
      modifyCuboidDataInPoi,
      shouldIncludeCuboids,
    ]
  );

  return (
    <Box height="100%">
      <StyledForm onSubmit={handleSubmit}>
        <Stack
          sx={{
            height: `calc(100vh / var(--zoom) - ${navbarHeight}px)`,
            overflowY: 'auto',
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          <Box p={2} flex={1} mb="85px">
            <PoiFormSubHeading variant="h5">Edit General</PoiFormSubHeading>
            
          
              
            
            {isVoi && <LinkedPoiEditor />}
            <GeneralFieldsForm
              teamsLoading={teamsLoading}
              teamOptions={teamOptions}
              dataHook={generalFieldsData}
              paintRegionOptions={paintRegionOptions}
              selectedPaintRegionId={selectedPaintRegionId}
              handlePaintRegionChange={handlePaintRegionChange}
            />
          </Box>
          <SaveCancelFormButtons
            cancelButtonLabel="Cancel"
            buttonLabel="Save"
            loadingLabel="Updating..."
            onCancel={() => {
              stopEditingPoiCuboids(editPointOfInterest?.pointOfInterest);
              cancelEditing();
            }}
            loading={loading}
            sx={saveCancelButtonCommonStyles.sx}
            saveButtonStyles={saveCancelButtonCommonStyles.saveButtonStyles}
            cancelButtonStyles={saveCancelButtonCommonStyles.cancelButtonStyles}
            saveButtonDisabled={isCuboidEditorEnabled}
          />
        </Stack>
      </StyledForm>
    </Box>
  );
};
